import { AccountManager } from '../../model/accountManager';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { SideBarItem, SidebarService, SIDEBAR_GROUP_LABELS } from '../../services/sidebar.service';
import { Subscription, filter } from 'rxjs';

import { NavigationEnd, Router } from '@angular/router';
import { StorageService } from '../../services/storage.service';
import { AuthService } from '../../services/auth.service';
import { MeService, roleLabel } from '@libs/auth';

@Component({
    selector: 'app-sidebar',
    imports: [],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy{
  private sideBarService = inject(SidebarService);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private authService = inject(AuthService);
  private meService = inject(MeService);

  sideBarItems = signal<SideBarItem[]>([]);
  subSideBarItems?: Subscription
  routerSub?: Subscription
  readonly showNav = signal(true)
  /** The URL owns the highlight. A signal, so a router event re-renders this view under OnPush. */
  private readonly activePath = signal('')
  userName = ''
  /**
   * the roles an administrator GRANTED, labelled (docs/ui/my-account-ui.md §2). One for nearly
   * everyone since identity inherits downward: an executive no longer needs Account Manager granted
   * to open a client. Not `effectiveRoles`, which would print the whole ladder; not "the highest",
   * because the ladder lives in identity and the client holds no copy (VT, 2026-09-07).
   */
  userRole = ''
  /** the name is the way into My account, once the app configures mePath */
  readonly myAccount = this.meService.enabled

  ngOnInit(): void {
    this.subSideBarItems = this.sideBarService.getSideBarItems().subscribe(
      data => {
        this.sideBarItems.set(data)
        this.syncSelection(this.router.url)
      }
    )

    // the URL owns the highlight, so deep links and back/forward stay in sync
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.syncSelection(e.urlAfterRedirects))

    this.showNav.set(this.storageService.getSideBarStatus())

    const user = this.storageService.getUser<AccountManager>()
    this.userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    this.userRole = (user?.roles ?? []).map(roleLabel).join(', ')
  }

  openMyAccount() {
    this.router.navigate(['/my-account'])
  }

  logOut() {
    this.authService.logOut()
    this.router.navigate(['/login'])
  }

  ngOnDestroy(): void {
      this.subSideBarItems?.unsubscribe()
      this.routerSub?.unsubscribe()
  }

  private syncSelection(url: string) {
    this.activePath.set(url)
  }

  isSelected(item: SideBarItem): boolean {
    const url = this.activePath()
    return url === item.path || url.startsWith(item.path + '/') || url.startsWith(item.path + '?')
  }

  toggleShowNav(){
    this.showNav.update(v => !v)
    this.storageService.saveSideBarStatus(this.showNav())
  }

  selectSideBarItem(sideBarItem: SideBarItem) {
    this.router.navigate([sideBarItem.path])
  }

  /** a divider + label goes before the first item of each group */
  isGroupStart(index: number): boolean {
    const item = this.sideBarItems()[index]
    if (!item?.group) return false
    return index === 0 || this.sideBarItems()[index - 1].group !== item.group
  }

  groupLabel(item: SideBarItem): string {
    return SIDEBAR_GROUP_LABELS[item.group]
  }

  navigateToHomePage(){
    this.router.navigate([''])
  }

}
